import parseLocale from '@/core/i18n/parse-locale';

/**
 * List of supported language IDs for the site.
 * Languages not in this list will be filtered out from the language selector
 * and their routes will redirect to the default locale.
 *
 * Note: Latvian (lv) is explicitly excluded from this list.
 */
export const SUPPORTED_LANGUAGE_IDS = [
  'bg', // Bulgarian
  'cs', // Czech
  'da', // Danish
  'de', // German
  'el', // Greek
  'en', // English
  'es', // Spanish
  'fi', // Finnish
  'fr', // French
  'hr', // Croatian
  'hu', // Hungarian
  'it', // Italian
  'lt', // Lithuanian
  'nl', // Dutch
  'no', // Norwegian
  'pl', // Polish
  'pt', // Portuguese
  'ro', // Romanian
  'sk', // Slovak
  'sr', // Serbian
  'sv', // Swedish
] as const;

export type SupportedLanguageId = (typeof SUPPORTED_LANGUAGE_IDS)[number];

/**
 * Checks if a language ID is supported.
 * @param languageId - The language ID to check (e.g., 'en', 'lv')
 * @returns true if the language is supported, false otherwise
 */
export const isSupportedLanguage = (languageId: string): boolean =>
  SUPPORTED_LANGUAGE_IDS.includes(languageId as SupportedLanguageId);

/**
 * Checks if a locale is supported based on its language component.
 * @param locale - The full locale string (e.g., 'en-nl', 'lv-lv')
 * @returns true if the locale's language is supported, false otherwise
 */
export const isSupportedLocale = (locale: string): boolean => {
  const { language } = parseLocale(locale);
  return isSupportedLanguage(language);
};
